import {
	getListingMarketType,
	listingUsesIncludedCaptain,
	type ListingMarketType
} from '$lib/listing-display';
import type { FaqSchemaItem } from '$lib/utils/marketing-seo';
import { BoatsKindOptions, ListingsKindOptions } from '@/pocketbase/pocketbase-types';

export interface ListingSeoPort {
	name?: string | null;
	city?: string | null;
	province?: string | null;
}

export interface ListingSeoFaqInput {
	kind: (typeof ListingsKindOptions)[keyof typeof ListingsKindOptions];
	capitanMode?: string | null;
	boatKind?: (typeof BoatsKindOptions)[keyof typeof BoatsKindOptions] | null;
	port?: ListingSeoPort | null;
	boatLength?: number | null;
	spots?: number | null;
	minPrice?: number | null;
	isExperience?: boolean;
}

export interface ListingSeoHubLink {
	label: string;
	href: string;
}

export interface ListingSeoIntro {
	heading: string;
	paragraphs: string[];
	hubLinks: ListingSeoHubLink[];
}

function boatTypeLabel(
	boatKind?: (typeof BoatsKindOptions)[keyof typeof BoatsKindOptions] | null
): string {
	if (boatKind === BoatsKindOptions.sailboat) return 'velero';
	if (boatKind === BoatsKindOptions.motorboat) return 'lancha';
	return 'barco';
}

function boatTypeLabelPlural(
	boatKind?: (typeof BoatsKindOptions)[keyof typeof BoatsKindOptions] | null
): string {
	if (boatKind === BoatsKindOptions.sailboat) return 'veleros';
	if (boatKind === BoatsKindOptions.motorboat) return 'lanchas';
	return 'barcos';
}

function locationLabel(port?: ListingSeoPort | null): string {
	return port?.city?.trim() || port?.name?.trim() || port?.province?.trim() || 'esta zona';
}

function portLabel(port?: ListingSeoPort | null): string {
	return port?.name?.trim() || port?.city?.trim() || 'el puerto';
}

export function getListingSeoIntro(
	input: ListingSeoFaqInput,
	hubLinks: ListingSeoHubLink[]
): ListingSeoIntro {
	const city = locationLabel(input.port);
	const tipo = boatTypeLabel(input.boatKind);
	const tipoPlural = boatTypeLabelPlural(input.boatKind);
	const marketType = getListingMarketType({
		kind: input.kind,
		capitanMode: input.capitanMode as never
	});
	const withCaptain = listingUsesIncludedCaptain({
		kind: input.kind,
		capitanMode: input.capitanMode as never
	});

	let heading: string;
	if (marketType === 'experience') {
		heading = `Salida en barco en ${city}`;
	} else if (withCaptain) {
		heading = `Alquiler de barco con patrón en ${city}`;
	} else if (input.boatKind === BoatsKindOptions.sailboat) {
		heading = `Alquiler de velero en ${city}`;
	} else {
		heading = `Alquiler de ${tipo} en ${city}`;
	}

	const pricePart = input.minPrice ? ` desde ${input.minPrice}€/día` : '';
	const capacityPart = input.spots ? ` para hasta ${input.spots} personas` : '';
	const lengthPart = input.boatLength ? ` de ${input.boatLength} metros` : '';

	const paragraphs: string[] = [];

	if (marketType === 'experience') {
		paragraphs.push(
			`Reserva una salida en barco en ${city}${pricePart}. Navega compartiendo la experiencia con el armador y otros marineros desde ${portLabel(input.port)}.`
		);
	} else if (withCaptain) {
		paragraphs.push(
			`Alquila un ${tipo}${lengthPart} en ${city}${capacityPart}${pricePart}. Patrón incluido: sal desde ${portLabel(input.port)} sin preocuparte del mando.`
		);
	} else {
		paragraphs.push(
			`Alquila ${tipo === 'lancha' ? 'una' : 'un'} ${tipo}${lengthPart} en ${city}${capacityPart}${pricePart}. Salida desde ${portLabel(input.port)}; necesitas la titulación náutica exigida para el mando.`
		);
		paragraphs.push(
			`En Zarpar encuentras ${tipoPlural} y más embarcaciones en ${city} con reserva online, pago seguro y cancelación flexible.`
		);
	}

	return { heading, paragraphs, hubLinks };
}

function bareboatFaqs(
	input: ListingSeoFaqInput,
	city: string,
	port: string,
	tipo: string
): FaqSchemaItem[] {
	const priceAnswer = input.minPrice
		? `Este anuncio está disponible desde ${input.minPrice}€/día. El precio final depende de las fechas que elijas al reservar.`
		: `El precio depende de las fechas seleccionadas. Consulta el calendario del anuncio para ver la tarifa exacta.`;

	return [
		{
			question: `¿Qué titulación necesito para alquilar esta ${tipo} en ${city}?`,
			answer: `Para llevar este barco sin patrón necesitas la titulación náutica exigida para su eslora y zona de navegación (por ejemplo PNB o superior). Zarpar no comprueba titulaciones; el tripulante es responsable de cumplir la normativa.`
		},
		{
			question: `¿Cuánto cuesta alquilar un barco en ${city}?`,
			answer: priceAnswer
		},
		{
			question: `¿Qué rutas se pueden hacer desde ${port}?`,
			answer: `Desde ${port} puedes explorar la costa y calas de ${city} y su provincia. Revisa la descripción del anuncio para rutas concretas recomendadas por el armador.`
		},
		{
			question: '¿El precio incluye combustible y patrón?',
			answer:
				'Este anuncio es sin patrón en el servicio: vas tú al timón si tienes titulación. El combustible suele no estar incluido salvo que el armador indique lo contrario en la descripción.'
		}
	];
}

function captainFaqs(
	input: ListingSeoFaqInput,
	city: string,
	port: string,
	tipo: string
): FaqSchemaItem[] {
	const priceAnswer = input.minPrice
		? `Desde ${input.minPrice}€/día con patrón incluido en este anuncio.`
		: `Consulta el calendario del anuncio para ver el precio según fechas.`;

	return [
		{
			question: `¿Incluye patrón el alquiler de barco en ${city}?`,
			answer: `Sí, este anuncio incluye patrón o capitán en el servicio. La salida es desde ${port} y no necesitas titulación para reservar.`
		},
		{
			question: `¿Cuánto cuesta alquilar un barco con patrón en ${city}?`,
			answer: priceAnswer
		},
		{
			question: `¿Para cuántas personas es este ${tipo}?`,
			answer: input.spots
				? `La embarcación tiene capacidad para hasta ${input.spots} personas. Indica el número de marineros al reservar.`
				: 'Consulta la ficha del anuncio para ver la capacidad máxima de la embarcación.'
		},
		{
			question: '¿Qué incluye el precio?',
			answer:
				'El patrón está incluido en el precio publicado. Combustible, extras o fianza pueden variar según el armador; revisa la descripción del anuncio.'
		}
	];
}

function experienceFaqs(input: ListingSeoFaqInput, city: string, port: string): FaqSchemaItem[] {
	const priceAnswer = input.minPrice
		? `La plaza en esta salida compartida está desde ${input.minPrice}€/día (precio por persona según fechas).`
		: 'El precio por persona depende de la fecha de la salida; consulta el calendario del anuncio.';

	return [
		{
			question: `¿Qué es una salida en barco en ${city}?`,
			answer: `Es una experiencia náutica compartida: navegas con el armador del barco y otros participantes, repartiendo los gastos de la jornada. La salida es desde ${port}.`
		},
		{
			question: `¿Cuánto cuesta una salida en barco en ${city}?`,
			answer: priceAnswer
		},
		{
			question: '¿Necesito experiencia previa para apuntarme?',
			answer:
				'No hace falta que lleves el barco: el armador actúa como patrón. Revisa la descripción para saber si hay requisitos de edad o condición física.'
		},
		{
			question: '¿Qué debo llevar a la salida?',
			answer:
				'Ropa cómoda, calzado antideslizante, protección solar y documento de identidad. El armador puede indicar material adicional en la descripción.'
		}
	];
}

function sailboatFaqs(
	input: ListingSeoFaqInput,
	city: string,
	withCaptain: boolean
): FaqSchemaItem[] {
	const base = withCaptain
		? captainFaqs(input, city, portLabel(input.port), 'velero')
		: bareboatFaqs(input, city, portLabel(input.port), 'velero');

	return [
		...base.slice(0, 2),
		{
			question: `¿Puedo alquilar un velero en ${city} sin experiencia?`,
			answer: withCaptain
				? 'Sí, con patrón incluido no necesitas manejar el velero tú mismo.'
				: 'Para llevar el velero sin patrón necesitas titulación y experiencia acorde a la embarcación y la zona.'
		},
		...base.slice(2)
	];
}

export function getListingFaqs(input: ListingSeoFaqInput): FaqSchemaItem[] {
	const city = locationLabel(input.port);
	const port = portLabel(input.port);
	const tipo = boatTypeLabel(input.boatKind);
	const marketType: ListingMarketType = getListingMarketType({
		kind: input.kind,
		capitanMode: input.capitanMode as never
	});
	const withCaptain = listingUsesIncludedCaptain({
		kind: input.kind,
		capitanMode: input.capitanMode as never
	});

	if (marketType === 'experience') {
		return experienceFaqs(input, city, port);
	}
	if (input.boatKind === BoatsKindOptions.sailboat) {
		return sailboatFaqs(input, city, withCaptain);
	}
	if (withCaptain) {
		return captainFaqs(input, city, port, tipo);
	}
	return bareboatFaqs(input, city, port, tipo);
}
